import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyGearItemDialog } from "../ModifyGearItemDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { GearItemCard } from "../GearItemCard";
import { GearItemDialog } from "../GearItemDialog";
import { useContextMenu } from "../../../../hooks/contextMenu";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";
import { useGetGearItems } from "../../../../hooks/gearItems/useGetGearItems";
import { IGearItem } from "../../../../hooks/gearItems";

export const GearItemsDesktop: FunctionComponent<IGearItemsDesktopProps> = ({
  handleClose,
  columnWidth,
  preselectedIds,
  onGearItemSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [gearItems, resetGearItems, setGearItems, noMoreGearItems] = useGetGearItems(setLoading);
  const [imageData, setImageData] = useState<string>();
  const {
    state: gearItemDialogState,
    key: gearItemDialogKey,
    openEdit: openGearItemDialog,
    close: closeGearItemDialog
  } = useResettableDialog<IGearItem>();
  const {
    state: modifyGearItemDialogState,
    key: modifyGearItemDialogKey,
    openAdd,
    openEdit,
    close: closeModifyGearItemDialog
  } = useResettableDialog<IGearItem>();
  const [searchText, setSearchText] = useState("");
  const [contextGearItem, setContextGearItem] = useState<IGearItem>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredGearItems = useMemo(() => {
    if (searchText == "") {
      return gearItems;
    }
    return gearItems?.filter(gearItem => {
      return (
        gearItem.name.toLowerCase().includes(searchText.toLowerCase()) ||
        gearItem.brand.toLowerCase().includes(searchText.toLowerCase()) ||
        gearItem.description.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [gearItems, searchText]);
  const onModifiedGearItem = (newGearItem: IGearItem) => {
    const newGearItems = gearItems.map(gearItem =>
      gearItem.uuid == newGearItem.uuid ? newGearItem : gearItem
    );
    newGearItems.sort(
      (a, b) =>
        a.status.localeCompare(b.status, undefined, { ignorePunctuation: true }) ||
        a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
    );
    setGearItems(newGearItems);
    toast.success(strings.generic_update_success);
  };
  const onDeletedGearItem = (deletedGearItem: IGearItem) => {
    const newGearItems = gearItems.filter(gearItem => gearItem.uuid != deletedGearItem.uuid);
    setGearItems(newGearItems);
    toast.success(strings.generic_update_success);
  };
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={false}
      items={filteredGearItems}
      emptyIconType={attributeIconMap[AttributeType.GEAR_ITEMS]}
      emptyString={strings.empty_gear_items}
      addItemString={strings.add_attribute}
      title={strings.gear_items_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={() => {
        setImageData(undefined);
        openAdd();
      }}
      refreshAction={resetGearItems}
      onClearValue={onClearValue}
    >
      {filteredGearItems?.map(gearItem => (
        <Grid item key={gearItem.uuid} xs={columnWidth}>
          <GearItemCard
            gearItem={gearItem}
            selected={preselectedIds?.includes(gearItem.uuid)}
            onClick={(_event, imageData) => {
              setImageData(imageData);
              if (onGearItemSelected) {
                onGearItemSelected(gearItem);
              } else {
                openEdit(gearItem);
              }
            }}
            onContextMenu={(event, imageData) => {
              setImageData(imageData);
              setContextGearItem(gearItem);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreGearItems) &&
        Array.from({ length: 40 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 80 }} className={styles.skeleton} />
          </Grid>
        ))}
      <ModifyGearItemDialog
        key={modifyGearItemDialogKey}
        open={modifyGearItemDialogState.mode !== "closed"}
        onClose={closeModifyGearItemDialog}
        gearItem={modifyGearItemDialogState.item}
        originalImageData={imageData}
        onNewItem={newGearItem => {
          const newGearItems = [...gearItems, newGearItem];
          newGearItems.sort(
            (a, b) =>
              a.status.localeCompare(b.status, undefined, { ignorePunctuation: true }) ||
              a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setGearItems(newGearItems);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={onModifiedGearItem}
        onDeletedItem={onDeletedGearItem}
      />
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openGearItemDialog(contextGearItem);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FrameInspect} />
          </ListItemIcon>
          {strings.gear_item_view}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextGearItem);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.gear_item_edit}
        </MenuItem>
      </Menu>
      {gearItemDialogState.item && (
        <GearItemDialog
          key={gearItemDialogKey}
          gearItem={gearItemDialogState.item}
          isOpen={gearItemDialogState.mode !== "closed"}
          onClose={closeGearItemDialog}
          canEdit={true}
          onModifiedItem={onModifiedGearItem}
          onDeletedItem={deletedItem => {
            closeGearItemDialog();
            onDeletedGearItem(deletedItem);
          }}
        />
      )}
    </AttributeDesktop>
  );
};

export interface IGearItemsDesktopProps {
  handleClose?: () => void;
  columnWidth: number;
  preselectedIds?: string[];
  onGearItemSelected?: (gearItem: IGearItem) => void;
  onClearValue?: () => void;
}
